import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Phone, CheckCircle2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { TurnstileField } from "../components/TurnstileField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAPTCHA_ON = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

const initial = { name: "", business_name: "", phone: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [token, setToken] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (CAPTCHA_ON && !token) {
      toast.error("Please complete the verification below.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, turnstile_token: token });
      setDone(true);
      setForm(initial);
      setToken("");
      toast.success("Thanks! We'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass =
    "w-full bg-gray-50 border border-gray-200 focus:border-sky-brand focus:ring-2 focus:ring-sky-brand/20 outline-none px-6 py-4 rounded-2xl font-body text-charcoal placeholder:text-gray-400 transition-all";

  return (
    <div data-testid="contact-page">
      <section className="bg-charcoal pt-40 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <span className="font-display text-sm uppercase tracking-widest font-semibold text-sky-brand">Contact</span>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight text-white mt-5">
              Get your free website preview
            </h1>
            <p className="font-body text-lg text-white/60 mt-6 max-w-2xl mx-auto">
              Tell us a little about your business and we'll craft a preview just for you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Info */}
          <Reveal className="lg:col-span-2">
            <h2 className="font-display text-2xl font-semibold text-charcoal">Let's talk</h2>
            <p className="font-body text-gray-500 mt-4 leading-relaxed">
              Prefer to reach out directly? We'd love to hear from you.
            </p>
            <ul className="mt-8 space-y-6">
              <li className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-sky-brand/15 flex items-center justify-center"><Mail className="text-sky-brand" size={22} /></div>
                <div><div className="font-display font-semibold text-charcoal">Email</div><div className="font-body text-gray-500 text-sm">elevatedweb.tx@gmail.com</div></div>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-sky-brand/15 flex items-center justify-center"><Phone className="text-sky-brand" size={22} /></div>
                <div><div className="font-display font-semibold text-charcoal">Phone</div><div className="font-body text-gray-500 text-sm">832-910-2910</div></div>
              </li>
            </ul>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="bg-white border border-gray-100 rounded-[2rem] p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
              {done ? (
                <div data-testid="contact-success" className="flex flex-col items-center text-center py-12">
                  <CheckCircle2 className="text-sky-brand" size={64} />
                  <h3 className="font-display text-2xl font-semibold text-charcoal mt-6">Message sent!</h3>
                  <p className="font-body text-gray-500 mt-3 max-w-sm">
                    Thanks for reaching out. Our team will get back to you shortly with your free website preview.
                  </p>
                  <button
                    data-testid="contact-reset-button"
                    onClick={() => setDone(false)}
                    className="font-display mt-8 bg-charcoal text-white hover:bg-sky-brand hover:text-charcoal transition-colors font-semibold px-6 py-3 rounded-full"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-display text-sm font-medium text-charcoal block mb-2">Name *</label>
                      <input data-testid="contact-name-input" required value={form.name} onChange={update("name")} placeholder="Your name" className={fieldClass} />
                    </div>
                    <div>
                      <label className="font-display text-sm font-medium text-charcoal block mb-2">Business Name</label>
                      <input data-testid="contact-business-input" value={form.business_name} onChange={update("business_name")} placeholder="Your business" className={fieldClass} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-display text-sm font-medium text-charcoal block mb-2">Phone Number *</label>
                      <input data-testid="contact-phone-input" required value={form.phone} onChange={update("phone")} placeholder="(555) 000-0000" className={fieldClass} />
                    </div>
                    <div>
                      <label className="font-display text-sm font-medium text-charcoal block mb-2">Email *</label>
                      <input data-testid="contact-email-input" type="email" required value={form.email} onChange={update("email")} placeholder="you@email.com" className={fieldClass} />
                    </div>
                  </div>
                  <div>
                    <label className="font-display text-sm font-medium text-charcoal block mb-2">Message *</label>
                    <textarea data-testid="contact-message-input" required value={form.message} onChange={update("message")} rows={5} placeholder="Tell us about your business and what you're looking for..." className={`${fieldClass} resize-none`} />
                  </div>
                  <TurnstileField onToken={setToken} className="pt-1" />
                  <button
                    type="submit"
                    disabled={submitting || (CAPTCHA_ON && !token)}
                    data-testid="contact-submit-button"
                    className="font-display w-full bg-sky-brand text-charcoal hover:bg-charcoal hover:text-white transition-colors duration-300 font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : <>Get My Free Preview <Send size={18} /></>}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
